/**
 * SPEC.md §6 "Import" — M5.6 (issue #26). Pure extension-dispatch logic for
 * drag-and-drop file import; the actual `dragover`/`drop` DOM wiring lives
 * in App.tsx (window-level, since it must work regardless of which pane
 * has focus) and hands off to this module.
 */

const PLAIN_TEXT_EXTENSIONS = new Set(['.mmd', '.mermaid', '.txt']);
const MARKDOWN_EXTENSION = '.md';

/** `foo.MMD` -> `.mmd`; `README` (no dot) -> `''`. */
function extensionOf(filename: string): string {
  const dot = filename.lastIndexOf('.');
  if (dot === -1) return '';
  return filename.slice(dot).toLowerCase();
}

/** Single source of truth for "is this a .md drop" — App.tsx uses this to pick the right failure message without re-deriving the extension check itself. */
export function isMarkdownFilename(filename: string): boolean {
  return extensionOf(filename) === MARKDOWN_EXTENSION;
}

function extractFirstMermaidFence(markdown: string): string | null {
  // [^\S\r\n]* tolerates trailing spaces/tabs after "mermaid" on the
  // opening fence line (e.g. a trailing space some editors add) without
  // also swallowing the newline that ends it.
  const match = /```mermaid[^\S\r\n]*\r?\n([\s\S]*?)\r?\n```/.exec(markdown);
  return match?.[1] ?? null;
}

/**
 * Returns the Mermaid source to load from a dropped file's name and text
 * content, or `null` for anything that isn't a supported case — an
 * unrecognised extension, or a `.md` file with no mermaid-fenced block.
 */
export function extractMermaidSource(filename: string, content: string): string | null {
  const extension = extensionOf(filename);
  if (PLAIN_TEXT_EXTENSIONS.has(extension)) return content;
  if (extension === MARKDOWN_EXTENSION) return extractFirstMermaidFence(content);
  return null;
}
